import { wallLengthLockConflict } from '../engine/wallLengthLocks.js';
import { createEditorHistorySession } from '../engine/editorHistorySession.js';
import SavedEditHistory from '../components/SavedEditHistory.jsx';
import NativeFieldTools from '../components/NativeFieldTools.jsx';
import WallHeightReview from '../components/WallHeightReview.jsx';
import FloorElevationReview from '../components/FloorElevationReview.jsx';
import OfflineSiteControls from '../components/OfflineSiteControls.jsx';
import { siteMode } from '../engine/siteAreas.js';
import { currentOfflineAccountKey, syncOfflineSites, attachOfflineEditorHistory } from '../engine/offlineSites.js';
import { isGroundLevel } from '../engine/siteGround.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link, useBlocker, useLocation } from 'react-router-dom';
import { api } from '../api.js';
import { useAuth, Loading } from '../App.jsx';
import { Mark } from '../components/Brand.jsx';
import ThemeToggle from '../components/ThemeToggle.jsx';
import Icon from '../components/Icon.jsx';
import CatalogueDialog from '../components/CatalogueDialog.jsx';
import PlacedModelUpdates from '../components/PlacedModelUpdates.jsx';
import Workspace from '../components/Workspace.jsx';
import { useToast } from '../components/Toast.jsx';
import { PlanDialog, ModelDialog, ReportDialog, CalibrateDialog, HistoryDialog, ShareDialog } from '../components/Dialogs.jsx';
import SiteBackup from '../components/SiteBackup.jsx';
import { PlanStore, usePlan } from '../store.js';
import { PdfImport, DxfImport } from '../components/DrawingImport.jsx';
import ReferenceDrawingReview, { prepareReferenceDrawing } from '../components/ReferenceDrawingReview.jsx';
import { exportDxf } from '../engine/drawingExchange.js';
import { blankDoc } from '../engine/constants.js';
import Modal from '../components/Modal.jsx';
import { readDraft, writeDraft, clearDraft } from '../engine/drafts.js';
import { setToolMode, useProfessional } from '../engine/toolMode.js';
import { AUTOSAVE_NOTE, autosaveDelay, autosaveEnabled, setAutosaveEnabled } from '../engine/autosave.js';
import ToolbarPanel from '../components/ToolbarPanel.jsx';
import { billOfMaterials } from '../engine/coverage.js';
import { totalLabel } from '../engine/costs.js';
import { beginActivity, withActivity } from '../activity.js';

const historyStorageFailure = error => ['history-limit', 'history-assets-unavailable', 'history-invalid'].includes(error?.code) || error?.name === 'QuotaExceededError';

export default function Editor({ sampleMode = false }) {
  const location = useLocation();
  const [sample, setSample] = useState(null), [sampleDraft, setSampleDraft] = useState(false);
  const bypassNavigation = useRef(false);
  const handoffConsumed = useRef(null);
  const loadedEditorIdentity = useRef(null);
  const { id } = useParams();
  const nav = useNavigate();
  const toast = useToast();
  const { user, logout } = useAuth();

  const storeRef = useRef(null);
  if (!storeRef.current) storeRef.current = new PlanStore(blankDoc());
  const store = storeRef.current;
  store.projectId = sampleMode ? null : id;
  store.sampleSource = sampleMode && !sampleDraft;
  usePlan(store);

  useEffect(() => {
    const report = () => { window.aviseraEditorDirty = store.dirty; };
    report(); const unsubscribe = store.subscribe(report);
    return () => { unsubscribe(); delete window.aviseraEditorDirty; };
  }, [store]);

  const stageRef = useRef(null);
  const controlsRef = useRef(null);
  const [planFindingRequest, setPlanFindingRequest] = useState(null);
  const [wallHeightRequest, setWallHeightRequest] = useState(null);
  const [floorElevationRequest, setFloorElevationRequest] = useState(null);
  const planNavigationEpoch = useRef(0);
  useEffect(() => {
    let account = currentOfflineAccountKey();
    const changed = () => {
      const current = currentOfflineAccountKey();
      if (current !== account) { account = current; planNavigationEpoch.current++; setPlanFindingRequest(null); }
    };
    window.addEventListener('avisera-offline-change', changed);
    return () => { planNavigationEpoch.current++; window.removeEventListener('avisera-offline-change', changed); };
  }, []);
  useEffect(() => { planNavigationEpoch.current++; setPlanFindingRequest(null); setWallHeightRequest(null); setFloorElevationRequest(null); }, [id, user.id, sampleMode]);
  const fileRef = useRef(null), cadRef = useRef(null);
  const [pdfFile, setPdfFile] = useState(null), [cadFile, setCadFile] = useState(null);
  const [drawingReview, setDrawingReview] = useState(null);
  const drawingPick = useRef(null), drawingSequence = useRef(0), editorIdentity = useRef(null);
  editorIdentity.current = { id, userID: user?.id, sampleMode };
  useEffect(() => {
    drawingSequence.current++; drawingPick.current = null; setDrawingReview(null); setPdfFile(null);
    return () => { drawingSequence.current++; };
  }, [id, user?.id, sampleMode]);
  useEffect(() => () => { if (drawingReview?.prepared.url) URL.revokeObjectURL(drawingReview.prepared.url); }, [drawingReview]);

  const [project, setProject] = useState(null);
  const savedProjectName = useRef('');
  const [rev, setRev] = useState(null);
  const [offlineVersion, setOfflineVersion] = useState(undefined);
  const [offlineRecord, setOfflineRecord] = useState(null);
  const receiveOfflineRecord = useCallback(record => setOfflineRecord(record), []);
  useEffect(() => {
    if (offlineRecord && offlineRecord.generation === offlineVersion) setRev(offlineRecord.project.rev);
  }, [offlineRecord, offlineVersion]);
  const [remoteChanged, setRemoteChanged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const saveInFlight = useRef(false);
  const [savedAt, setSavedAt] = useState(null);
  const professional = useProfessional();
  const [autosave, setAutosave] = useState(autosaveEnabled), [autosaveFailures, setAutosaveFailures] = useState(0);
  const autosaveFailureCount = useRef(0); autosaveFailureCount.current = autosaveFailures;
  const [saveError, setSaveError] = useState('');
  const [themeRev, setThemeRev] = useState(0);
  const [focus, setFocus] = useState(false);
  const [dimension, setDimension] = useState('2d');
  const [sampleGuideOpen, setSampleGuideOpen] = useState(false);
  const [siteActionsOpen, setSiteActionsOpen] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);
  const [savedHistoryOpen, setSavedHistoryOpen] = useState(false);
  const historySession = useRef(null);
  const [historySaveIssue, setHistorySaveIssue] = useState(null);
  const [historyStatus, setHistoryStatus] = useState({ eligible: false, state: 'unavailable', reason: '' });
  const [loadError, setLoadError] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const blocker = useBlocker(({ currentLocation, nextLocation }) => !bypassNavigation.current && store.dirty && currentLocation.pathname !== nextLocation.pathname);

  const [dlg, setDlg] = useState(null);          // 'plan' | 'report' | 'history' | 'share' | 'calib'
  const [modelDlg, setModelDlg] = useState(null); // { id } | null
  const [pendingCal, setPendingCal] = useState(null);
  const [versions, setVersions] = useState(null);
  const [shares, setShares] = useState(null);

  /* ---- load ---- */
  useEffect(() => {
    let live = true;
    loadedEditorIdentity.current = null;
    historySession.current?.dispose(); historySession.current = null;
    setHistoryStatus({ eligible: false, state: 'unavailable', reason: '' });
    setOfflineRecord(null); setSavedHistoryOpen(false); setHistorySaveIssue(null);
    const account = currentOfflineAccountKey(), userID = user.id;
    const assertCurrent = () => {
      const current = editorIdentity.current;
      if (!live || current.id !== id || current.userID !== userID || current.sampleMode !== sampleMode || currentOfflineAccountKey() !== account) throw Error('The owning Site editor changed. Reopen the Site to continue.');
    };
    const session = sampleMode ? null : createEditorHistorySession({ store, projectID: id, bridge: { attach: attachOfflineEditorHistory },
      assertCurrent, controller: () => navigator.serviceWorker?.controller, onChange: value => { if (live) setHistoryStatus(value); } });
    historySession.current = session;
    const changedController = () => session?.detach('The offline editor was updated. Reopen this Site to restore saved Undo.');
    navigator.serviceWorker?.addEventListener('controllerchange', changedController);
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const r = sampleMode ? await api.samples.read(id).then(r => ({ ...r, project: { id: null, name: r.sample.name, rev: 1 } })) : await api.projects.read(id);
        // Reading an old revision may link previously unused legacy hardware.
        const [catalogueResult] = await Promise.allSettled([sampleMode ? Promise.resolve({ models: [...new Map([r.doc, ...(r.doc.floors || []).map(f=>f.plan)].flatMap(f=>f.models).map(m=>[m.id,m])).values()] }) : api.catalogues.select({ scope: 'site', projectId: id }, null, {})]);
        if (!live) return;
        const catalogue = catalogueResult.status === 'fulfilled' ? catalogueResult.value : { models: [] };
        if (catalogueResult.status === 'rejected') toast('Plan opened, but the camera library could not load. Browse the library to try again.', 'bad');
        setProject(r.project); savedProjectName.current = r.project.name;
        setSample(r.sample || null); setSampleDraft(false); bypassNavigation.current = false;
        setRev(r.project.rev ?? 0); setRemoteChanged(false);
        setOfflineVersion(r.offlineVersion);
        const doc = { ...blankDoc(), ...r.doc };
        store.projectId = sampleMode ? null : id;
        if(sampleMode)store.viewLabels='essential';
        store.setSiteModels(catalogue.models);
        const handoff = !sampleMode && location.state?.sampleHandoff;
        store.setDoc(handoff?.doc || doc, { markClean: !handoff?.dirty });
        if(handoff?.dialog) { setDlg(handoff.dialog); if(handoff.dialog === 'share') setShares([]); if(handoff.dialog === 'history') api.projects.history(id).then(r=>setVersions(r.versions)).catch(e=>toast(e.message,'bad')); }
        if(handoff?.modelName) setModelDlg({ id: catalogue.models.find(m=>m.name===handoff.modelName)?.id || null });
        const draft = readDraft(user.id, sampleMode ? 'sample:' + id : id);
        setPendingDraft(draft && JSON.stringify(draft.doc) !== JSON.stringify(store.toDocument()) ? draft : null);
        store.newModelId = catalogue.models.find(m => m.id === doc.models[0]?.id)?.id || catalogue.models[0]?.id || '';
        if (session) await session.open({ document: store.toDocument(), rev: r.project.rev ?? 0, offlineVersion: r.offlineVersion,
          ...(handoff?.dirty ? { skipReason: 'This recovered working copy uses current-session Undo. Earlier saved history remains available for review.' } : {}) });
        if (!live) return;
        await loadBackground(handoff?.doc || doc);
        if (!live) return;
        loadedEditorIdentity.current = { id, sampleMode };
        setTimeout(() => stageRef.current?.fit(), 30);
      } catch (e) {
        setLoadError(e.message);
        if (e.status === 404) nav('/', { replace: true });
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; session?.dispose(); navigator.serviceWorker?.removeEventListener('controllerchange', changedController); store.cancelEvaluation(); };
  }, [id, user.id, sampleMode, loadAttempt]); // eslint-disable-line react-hooks/exhaustive-deps

  // A queue acknowledgement rotates the stored token without changing live
  // Undo or newer unsaved edits. Refresh only this editor's accepted generation.
  useEffect(() => {
    if (loading || saving || saveInFlight.current || sampleMode || offlineRecord?.project?.id !== id || offlineRecord.generation !== offlineVersion) return;
    void historySession.current?.refresh({ document: offlineRecord.doc, rev: offlineRecord.project.rev, offlineVersion: offlineRecord.generation });
  }, [offlineRecord, offlineVersion, loading, saving, sampleMode, id]);

  useEffect(() => {
    if (loading || loadError || pendingDraft) return;
    let timer, warned = false;
    const flush = () => {
      clearTimeout(timer);
      if (!store.dirty) { clearDraft(user.id, sampleMode ? 'sample:' + id : id); return; }
      try { writeDraft(user.id, sampleMode ? 'sample:' + id : id, store.toDocument(), rev); }
      catch { if (!warned) { warned = true; toast('This browser could not keep a recovery draft. Save your changes to the server.', 'bad'); } }
    };
    const unsubscribe = store.subscribe(() => { clearTimeout(timer); timer = setTimeout(flush, 250); });
    flush();
    const onHidden = () => { if (document.visibilityState === 'hidden') flush(); };
    window.addEventListener('pagehide', flush); document.addEventListener('visibilitychange', onHidden);
    return () => { unsubscribe(); window.removeEventListener('pagehide', flush); document.removeEventListener('visibilitychange', onHidden); flush(); };
  }, [loading, loadError, pendingDraft, id, user.id, rev, store, toast]);

  const loadBackground = useCallback(async doc => {
    if (!doc.bg?.imageId) { store.bgImage = null; store.touch({ dirty: false, reevaluate: false }); return; }
    const activity = beginActivity('Loading floor drawing…');
    try { await new Promise(resolve => {
      const img = new Image();
      img.onload = () => { if (store.doc.bg?.imageId !== doc.bg.imageId) { resolve(); return; } store.bgImage = img; store.touch({ dirty: false, reevaluate: false }); resolve(); };
      img.onerror = () => { store.bgImage = null; resolve(); };
      img.src = api.images.url(doc.bg.imageId);
    }); } finally { activity.finish(); }
  }, [store]);

  useEffect(() => {
    if (sampleMode && !loading && store.dirty && !sampleDraft) {
      setSampleDraft(true); setProject(p => ({ ...p, name: 'Untitled site' }));
      toast('You are editing an Untitled working copy. Save it to Your sites.', 'ok');
    }
  }, [sampleMode, loading, store.rev, sampleDraft]);

  useEffect(() => {
    const handoff = location.state?.sampleHandoff;
    // A route change initially retains the previous route’s loading=false.
    // Wait for this owned Site to finish loading before capturing its review
    // fingerprint; otherwise the sample document can become the review base.
    const loaded = loadedEditorIdentity.current;
    if (!sampleMode && !loading && loaded?.id === id && loaded.sampleMode === sampleMode && handoff && handoffConsumed.current !== id) {
      handoffConsumed.current = id;
      if (handoff.image) placeBackground(handoff.image, handoff.imageSource).catch(e => toast(e.message, 'bad'));
      // Consume transient work exactly once. Reloading a saved copy must not
      // reopen its dialog or restore an older document from navigation state.
      const { sampleHandoff, ...state } = location.state;
      nav(location.pathname + location.search + location.hash, { replace: true, state });
    }
  }, [id, loading, sampleMode, location.state]);

  // Network-backed actions first create an owned site. Concurrent canvas edits
  // are handed to the destination editor and remain dirty until saved there.
  const materializeSample = useCallback(async (continuation = {}) => {
    if (saveInFlight.current) return false;
    saveInFlight.current = true; setSaving(true); setSaveError('');
    try {
      const before = JSON.stringify(store.toDocument());
      const r = await api.projects.create(sampleDraft ? project?.name || 'Untitled site' : 'Untitled site', JSON.parse(before));
      const latest = store.toDocument(), dirty = JSON.stringify(latest) !== before;
      if (continuation.leave && dirty) {
        // The saved snapshot is valid, but leaving would discard edits made
        // while that request was in flight. Keep the current recovery draft.
        store.touch({ dirty: true, reevaluate: false });
        toast('The copy was saved. Newer edits are still here; save them before leaving.', 'ok');
        return false;
      }
      clearDraft(user.id, 'sample:' + id);
      store.markSaved();
      if (continuation.leave) return !dirty;
      bypassNavigation.current = true;
      nav('/p/' + r.project.id, { replace: true, state: { sampleHandoff: { ...continuation, ...(dirty ? { doc: latest, dirty: true } : {}) } } });
      return !dirty;
    } catch (e) { setSaveError(e.message); toast(e.message, 'bad'); return false; }
    finally { saveInFlight.current = false; setSaving(false); }
  }, [id, sampleDraft, project?.name, store, user.id, nav, toast]);

  /* ---- save ---- */
  const save = useCallback(async (note = '', { planOnly = false, automatic = false } = {}) => {
    if (automatic) note = AUTOSAVE_NOTE;
    if (sampleMode) return materializeSample({ leave: blocker.state === 'blocked' });
    if (saveInFlight.current) return false;
    saveInFlight.current = true;
    setSaving(true);
    setSaveError('');
    let retainingHistory = false;
    try {
      const session = historySession.current, context = session?.getSaveContext();
      const identity = { ...editorIdentity.current }, account = currentOfflineAccountKey();
      const assertCurrent = () => {
        const current = editorIdentity.current;
        if (historySession.current !== session || current.id !== identity.id || current.userID !== identity.userID || current.sampleMode !== identity.sampleMode || currentOfflineAccountKey() !== account) throw Error('The owning Site editor changed while saving.');
      };
      retainingHistory = !!context?.eligible && !planOnly;
      const liveDocument = JSON.stringify(store.toDocument());
      const state = retainingHistory ? store.exportHistoryState() : null;
      const savedDoc = JSON.stringify(state?.present || JSON.parse(liveDocument));
      const r = state
        ? await api.projects.saveWithHistory(id, JSON.parse(savedDoc), note, rev, offlineVersion, state, context.historyAnchor, assertCurrent)
        : await api.projects.save(id, JSON.parse(savedDoc), note, rev, offlineVersion);
      if (r.historyAccepted) session.accept(r, JSON.parse(savedDoc));
      // Preserve an accepted device save even when its original editor closed.
      try { assertCurrent(); } catch { return false; }
      setOfflineVersion(r.offlineVersion);
      setRev(r.rev); setRemoteChanged(false);
      // Structured history has canonical key order; compare the live document
      // with its own captured representation when deciding whether edits remain.
      if (JSON.stringify(store.toDocument()) === liveDocument) store.markSaved();
      if (planOnly) session?.detach('Plan saved with current-session Undo. Earlier saved history is retained for review.');
      setHistorySaveIssue(null);
      setSavedAt(new Date()); setAutosaveFailures(0);
      if (!store.dirty) clearDraft(user.id, sampleMode ? 'sample:' + id : id);
      if (r.historyAccepted) void syncOfflineSites().catch(() => {});
      return !store.dirty;
    } catch (e) {
      setSaveError(e.message);
      if (retainingHistory && historyStorageFailure(e)) setHistorySaveIssue({ message: e.message, drawing: false });
      if (e.status === 409 && !historyStorageFailure(e)) setRemoteChanged(true);
      // A failed autosave waits and retries quietly; the status line says so. Only the first failure is announced.
      if (automatic) { if (!autosaveFailureCount.current) toast('Autosave could not save: ' + e.message + ' Your changes are kept on this device.', 'bad'); setAutosaveFailures(n => n + 1); }
      else toast(e.message, 'bad');
      return false;
    } finally {
      saveInFlight.current = false;
      setSaving(false);
    }
  }, [id, rev, offlineVersion, saving, store, toast, user.id, sampleMode, materializeSample, blocker.state]);

  // Autosave: a few seconds after the last edit, save by itself. The server folds
  // one session's autosaves into a single History entry.
  const saveRef = useRef(save); saveRef.current = save;
  // Offline, or while earlier saves wait to upload, each autosave would queue another
  // full copy of the Site on the device. The recovery draft already protects the edits,
  // so autosave waits and Save stays the way to keep a version on the device.
  const [online, setOnline] = useState(() => navigator.onLine !== false);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine !== false);
    window.addEventListener('online', update); window.addEventListener('offline', update);
    return () => { window.removeEventListener('online', update); window.removeEventListener('offline', update); };
  }, []);
  const uploadsWaiting = !!(offlineRecord?.creation || offlineRecord?.pending?.length || offlineRecord?.blocked);
  const autosaveBlocked = !autosave || sampleMode || loading || !!loadError || !!pendingDraft || remoteChanged || !!historySaveIssue || !online || uploadsWaiting;
  useEffect(() => {
    if (autosaveBlocked) return;
    let timer;
    const attempt = () => {
      if (!store.dirty) return;
      // Wait for a field edit, a drawing in progress or a running save to finish.
      if (store._edit || saveInFlight.current || document.querySelector('dialog[open]')) { timer = setTimeout(attempt, 1500); return; }
      void saveRef.current('', { automatic: true });
    };
    // Only a change to the plan restarts the wait; selection and view changes do not.
    let seen = null;
    const schedule = () => { if (store.changes === seen) return; seen = store.changes; clearTimeout(timer); if (store.dirty) timer = setTimeout(attempt, autosaveDelay(autosaveFailures)); };
    const unsubscribe = store.subscribe(schedule);
    schedule();
    return () => { clearTimeout(timer); unsubscribe(); };
  }, [store, autosaveBlocked, autosaveFailures, rev]);

  const commitReferenceDrawing = useCallback(async ({ reviewed, attempt, image, assertCurrent }) => {
    if (sampleMode || saveInFlight.current) throw new Error('Wait for the current save before importing this drawing.');
    assertCurrent();
    const candidate = JSON.parse(JSON.stringify(store.toDocument(reviewed.doc)));
    saveInFlight.current = true; setSaving(true); setSaveError('');
    let retainingHistory = false;
    try {
      // The live plan, Undo history and old drawing stay untouched until the
      // image bytes and candidate version have committed in one transaction.
      const session = historySession.current, context = session?.getSaveContext();
      retainingHistory = !!context?.eligible;
      const sourceState = retainingHistory ? store.exportHistoryState() : null;
      const state = sourceState ? store.exportHistoryState({ candidate }) : null;
      const receipt = state
        ? await api.projects.saveReferenceWithHistory(id, candidate, 'Reviewed reference drawing', rev, offlineVersion, attempt, sourceState, state, context.historyAnchor, assertCurrent)
        : await api.projects.saveReference(id, candidate, 'Reviewed reference drawing', rev, offlineVersion, attempt, assertCurrent);
      if (receipt.historyAccepted) session.accept(receipt, candidate);
      if (!receipt.referenceAccepted) throw new Error('This device did not confirm saving the drawing. Keep the review open and retry.');
      // An accepted transaction must never be presented as a failed retry. A
      // route/account change or newer local edit keeps its current live draft.
      try { assertCurrent(); } catch { return { ...receipt, applied: false }; }
      store.endEdit(); store.snapshot(); store.doc = reviewed.doc; store.bgImage = image;
      store.touch({ dirty: false }); store.markSaved();
      setOfflineVersion(receipt.offlineVersion); setRev(receipt.rev); setRemoteChanged(false); setSavedAt(new Date());
      clearDraft(user.id, id); setHistorySaveIssue(null);
      // Foreground retries begin after the accepted state is visible. A failed
      // upload leaves the durable queue intact and is reported by Site controls.
      void syncOfflineSites().catch(() => {});
      return { ...receipt, applied: true };
    } catch (error) {
      if (retainingHistory && historyStorageFailure(error)) setHistorySaveIssue({ message: error.message, drawing: true });
      throw error;
    } finally { saveInFlight.current = false; setSaving(false); }
  }, [id, rev, offlineVersion, sampleMode, store, user.id]);

  useEffect(() => {
    function onKey(e) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        const reviewing = e.defaultPrevented || document.querySelector('dialog[open]');
        e.preventDefault();
        if (!reviewing) save();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [save]);

  // the browser's own guard is the only one that reliably survives a tab close
  useEffect(() => {
    const onLeave = e => { if (store.dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [store]);

  useEffect(() => {
    if (sampleMode || loading || loadError || pendingDraft || offlineRecord?.creation || offlineRecord?.pending.length) return;
    let live = true, checking = false;
    const check = async () => {
      if (checking || saveInFlight.current || document.hidden) return;
      checking = true;
      try {
        const result = await api.projects.revision(id);
        if (!live || saveInFlight.current || result.rev === rev) return;
        setRemoteChanged(true);
      } catch { /* A later check or Save will report connectivity/access failures. */ }
      finally { checking = false; }
    };
    const timer = setInterval(check, 30000);
    window.addEventListener('focus', check);
    return () => { live = false; clearInterval(timer); window.removeEventListener('focus', check); };
  }, [id, rev, sampleMode, loading, loadError, pendingDraft, offlineRecord]);

  /* ---- rename ---- */
  const rename = useCallback(async name => {
    if (sampleMode) { setProject(p => ({ ...p, name })); if(sampleDraft) store.touch({ reevaluate: false }); return; }
    if (offlineRecord?.creation) { toast('This Site is saved on your device. Rename it after its first upload completes.', 'bad'); return; }
    try { await api.projects.update(id, { name }); savedProjectName.current = name; setProject(p => ({ ...p, name })); }
    catch (e) { setProject(p => ({ ...p, name: savedProjectName.current })); toast(e.message, 'bad'); }
  }, [id, toast, sampleMode, sampleDraft, store, offlineRecord]);

  /* ---- background image ---- */
  function drawingTarget() {
    const view = stageRef.current?.view(), k = view?.k || 30;
    return { projectID: id, userID: user?.id, accountKey: currentOfflineAccountKey(), nativeAccountID: window.aviseraNativeCapabilities?.accountID, sampleMode, floorID: store.doc.activeFloorId,
      fingerprint: JSON.stringify(store.toDocument()), plan: structuredClone(store.doc),
      position: { x: view ? view.ox + 60 / k : 0, y: view ? view.oy + 60 / k : 0 },
      widthMetres: Math.max(80, (stageRef.current?.canvas()?.clientWidth || 800) - 120) / k,
      sequence: ++drawingSequence.current };
  }
  function drawingTargetCurrent(target) {
    const current = editorIdentity.current;
    return current.id === target.projectID && current.userID === target.userID && current.sampleMode === target.sampleMode && drawingSequence.current === target.sequence && currentOfflineAccountKey() === target.accountKey && window.aviseraNativeCapabilities?.accountID === target.nativeAccountID;
  }
  async function onPickImage(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const target = drawingTarget(); drawingPick.current = target;
    if (/\.pdf$/i.test(file.name) || file.type === 'application/pdf') { setPdfFile(file); return; }
    try { await placeBackground(file, undefined, target); } catch (err) { toast(err.message, 'bad'); }
  }

  async function placeBackground(file, source = { kind: 'image', fileName: file.name }, target = drawingTarget()) {
    const activity = beginActivity('Preparing reference drawing…', { delay: 0 });
    let prepared;
    try {
      prepared = await prepareReferenceDrawing(file);
      if (!drawingTargetCurrent(target) || store.doc.activeFloorId !== target.floorID || JSON.stringify(store.toDocument()) !== target.fingerprint) throw new Error('The Site or floor changed while the drawing was opening. Select it again on the intended floor.');
      // Samples still materialize an owned Site before an image upload. Carry
      // the original PDF filename/page through that existing editor handoff.
      if (sampleMode) { await materializeSample({ image: file, imageSource: source }); return; }
      const next = { prepared: { ...prepared, source }, target: { ...target, placement: { ...target.position, mpp: target.widthMetres / prepared.width } } };
      setDrawingReview(next); prepared = null;
    } finally {
      if (prepared?.url) URL.revokeObjectURL(prepared.url);
      activity.finish();
    }
  }

  /* ---- calibrate ---- */
  const onCalibrate = useCallback(payload => {
    setPendingCal(payload);
    setDlg('calib');
  }, []);

  function applyCalibration(target) {
    if (!pendingCal || !(target > 0)) return;
    const f = target / pendingCal.metresNow;
    const a = pendingCal.anchor;
    const S = store.doc;
    if (!S.bg && S.cams.some(c => c.locked)) { toast('Unlock cameras before rescaling the plan geometry.', 'bad'); return; }
    if(!S.bg)for(const wall of S.walls){
      const conflict=wallLengthLockConflict(S,wall,{x1:a.x+(wall.x1-a.x)*f,y1:a.y+(wall.y1-a.y)*f,x2:a.x+(wall.x2-a.x)*f,y2:a.y+(wall.y2-a.y)*f});
      if(conflict){toast(conflict,'bad');return;}
    }
    store.snapshot();
    if (S.bg) {
      S.bg.mpp *= f;
      S.bg.x = a.x + (S.bg.x - a.x) * f;
      S.bg.y = a.y + (S.bg.y - a.y) * f;
    } else {
      const sc = p => ({ x: a.x + (p.x - a.x) * f, y: a.y + (p.y - a.y) * f });
      S.walls.forEach(w => {
        const p = sc({ x: w.x1, y: w.y1 }), q = sc({ x: w.x2, y: w.y2 });
        w.x1 = p.x; w.y1 = p.y; w.x2 = q.x; w.y2 = q.y;
      });
      S.areas.forEach(o => { o.pts = o.pts.map(sc); });
      S.zones.forEach(o => { o.pts = o.pts.map(sc); });
      S.cams.forEach(c => { const p = sc(c); c.x = p.x; c.y = p.y; });
      S.equipment.forEach(c => Object.assign(c, sc(c)));
      S.targets.forEach(c => Object.assign(c, sc(c)));
      S.openings.forEach(o => { o.along *= f; o.width *= f; });
      S.tasks.forEach(t => { Object.assign(t, sc(t)); for (const key of ['length', 'width', 'offset']) if (t[key] != null) t[key] *= f; });
      S.obstacles.forEach(o => { Object.assign(o, sc(o)); o.width *= f; o.depth *= f; });
      S.cables.forEach(c => { c.pts = c.pts.map(sc); });
      S.viewpoints.forEach(v => { for (const key of ['position', 'target']) { const p = sc({ x: v[key][0], y: v[key][2] }); v[key][0] = p.x; v[key][2] = p.y; } });
    }
    store.setTool('select');
    S.setup = { scaleConfirmed: true };
    store.touch();
    setTimeout(() => stageRef.current?.fit(), 20);
  }

  const [libraryReturn, setLibraryReturn] = useState(null);
  /* ---- camera library ---- */
  const onSaveModel = useCallback(async ({ id: modelId, model, revision: expected }) => {
    const saved = modelId ? (await api.catalogues.update(modelId, model, expected)).model : (await api.catalogues.create({ scope: 'site', projectId: id }, model)).model;
    store.newModelId = saved.id; store.rememberModels([saved]);
    toast('Site camera definition saved. Placed cameras retain their saved specifications.', 'ok');
  }, [store, id, toast]);

  const onDeleteModel = useCallback(async modelId => {
    const m = store.catalogue.get(modelId);
    await api.catalogues.remove(modelId, m.catalogue.revision);
    store.setSiteModels([...store.catalogue.values()].filter(m => m.id !== modelId));
    toast('Removed from the site library. Placed cameras are preserved.', 'ok');
  }, [store, toast]);

  const onSyncModels = useCallback(async () => {
    if(sampleMode) { await materializeSample({ dialog: 'modelUpdates' }); return; }
    try {
      const r = await api.catalogues.select({ scope: 'site', projectId: id }, null, {});
      store.setSiteModels(r.models); setDlg('modelUpdates');
    } catch (e) { toast(e.message, 'bad'); }
  }, [store, id, toast, sampleMode, materializeSample]);
  const openCatalogue = () => sampleMode ? materializeSample({ dialog: 'catalogue' }) : setDlg('catalogue');
  store.openCatalogue = (returnTo = null) => { setLibraryReturn(returnTo); openCatalogue(); };
  store.reviewCatalogueUpdates = onSyncModels;

  /* ---- history & shares ---- */
  async function openHistory() {
    if(sampleMode) { await materializeSample({ dialog: 'history' }); return; }
    setVersions(null);
    setDlg('history');
    try { const r = await api.projects.history(id); setVersions(r.versions); }
    catch (e) { toast(e.message, 'bad'); }
  }
  async function restore(targetRev) {
    if (saveInFlight.current) return;
    if(store.dirty||offlineRecord?.pending.length||offlineRecord?.creation){toast('Save your changes and finish uploading before restoring online history. Your current edits are kept here.','bad');return;}
    saveInFlight.current = true; setSaving(true);
    const before = JSON.stringify(store.toDocument());
    try {
      const r = await api.projects.restore(id, targetRev, rev);
      if (JSON.stringify(store.toDocument()) !== before) {
        setRemoteChanged(true); setDlg(null);
        toast('The server version was restored. Your newer local edits are kept here for review.', 'ok');
        return;
      }
      const doc = { ...blankDoc(), ...r.doc };
      historySession.current?.detach('An online version was restored. Earlier saved Undo remains available for review.');
      store.setDoc(doc);
      await loadBackground(doc);
      setRev(r.rev); setRemoteChanged(false);
      setDlg(null);
      setTimeout(() => stageRef.current?.fit(), 30);
      toast(`Restored r${targetRev} as r${r.rev}.`, 'ok');
    } catch (e) { if (e.status === 409) setRemoteChanged(true); toast(e.message, 'bad'); }
    finally { saveInFlight.current = false; setSaving(false); }
  }
  async function openShares() {
    if(sampleMode) { await materializeSample({ dialog: 'share' }); return; }
    setShares(null);
    setDlg('share');
    try { const r = await api.shares.list(id); setShares(r.shares); }
    catch (e) { toast(e.message, 'bad'); }
  }
  async function createShare(body) {
    try {
      await api.shares.create(id, body);
      const r = await api.shares.list(id);
      setShares(r.shares);
      toast('Share link created.', 'ok'); return true;
    } catch (e) { toast(e.message, 'bad'); return false; }
  }
  async function revokeShare(token) {
    try {
      await api.shares.revoke(token);
      const r = await api.shares.list(id);
      setShares(r.shares);
      toast('Link revoked.', 'ok');
    } catch (e) { toast(e.message, 'bad'); }
  }

  /* ---- png ---- */
  function exportPng() {
    controlsRef.current?.openExport();
  }
  async function exportDrawing() {
    const doc = structuredClone(store.doc);
    try { await withActivity('Preparing DXF export…', () => {
      const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([exportDxf(doc)], { type: 'application/dxf' }));
      a.download = 'avisera-planner-' + doc.activeFloorId + '.dxf'; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 500);
    }); } catch (e) { toast(e.message, 'bad'); }
  }

  const saveLabel = useMemo(() => {
    if (saving) return 'Saving…';
    if (store.dirty) return autosaveBlocked ? autosave && !sampleMode && (!online || uploadsWaiting) ? 'Unsaved changes · autosave resumes online' : 'Unsaved changes' : autosaveFailures ? 'Autosave paused · changes kept on this device' : 'Saving shortly…';
    if (offlineRecord?.creation || offlineRecord?.pending.length) return offlineRecord.blocked ? 'Saved on device · upload needs review' : 'Saved on device · waiting to upload';
    if (savedAt) return 'Saved ' + savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return sampleMode ? sampleDraft ? 'Working copy · not saved yet' : 'Sample · original protected' : 'Up to date';
  }, [saving, store.dirty, store.rev, savedAt, sampleMode, sampleDraft, offlineRecord, autosaveBlocked, autosaveFailures, autosave, online, uploadsWaiting]); // eslint-disable-line react-hooks/exhaustive-deps

  const openWallHeight = (wallID, returnFocusRef) => {
    if (sampleMode) return;
    if (saving || saveInFlight.current || loading || pendingDraft || remoteChanged) {
      toast('Finish the current save or Site review before changing wall height.', 'bad');
      return;
    }
    if (!store.doc.walls.some(w => w.id === wallID)) {
      toast('Choose a wall on this floor before reviewing its height.', 'bad');
      return;
    }
    setWallHeightRequest({ wallID, floorID: store.doc.activeFloorId, returnFocusRef });
  };
  const openFloorElevation = (floorID, returnFocusRef) => {
    if (sampleMode) return;
    if (saving || saveInFlight.current || loading || pendingDraft || remoteChanged) {
      toast('Finish the current save or Site review before changing floor elevation.', 'bad');
      return;
    }
    if (floorID !== store.doc.activeFloorId || isGroundLevel(store.doc)) {
      toast('Choose an upper floor or basement in this building before reviewing its elevation.', 'bad');
      return;
    }
    setFloorElevationRequest({ floorID, returnFocusRef });
  };

  if (loading) return <div className="centred"><Loading what="Opening the plan" /></div>;
  if (loadError) return <div className="centred"><div className="card"><h1>Could not open this site</h1><p role="alert">{loadError}</p><button className="btn primary" onClick={() => setLoadAttempt(x => x + 1)}>Try again</button><Link className="btn" to="/">Your sites</Link></div></div>;

  return (
    <div className="app editor-app" data-dimension={dimension}>
      <header className="topbar">
        <Link to="/" aria-label="Avisera Planner sites" className="brand" style={{ textDecoration: 'none', color: 'inherit' }}><Mark /></Link>
        <span className="breadcrumb-divider">/</span>
        <div className="doc-title">
          <input value={project?.name || ''} aria-label="Project name" readOnly={sampleMode && !sampleDraft || !!offlineRecord?.creation} title={offlineRecord?.creation ? 'Rename after the first upload completes' : undefined}
            onChange={e => setProject(p => ({ ...p, name: e.target.value }))}
            onBlur={e => rename(e.target.value.trim() || 'Untitled site')} />
          <span className={'rev' + (store.dirty ? ' dirty' : '')}>{sampleMode ? sampleDraft ? 'Draft' : 'Sample' : offlineRecord?.creation ? 'On device' : 'r' + (rev ?? 1)}</span>
        </div>

        <div className="spacer" />
        <span className="save-state" title={saveLabel}>{saveLabel}</span>

        <div className="topbar-actions">
          <span className="vsep" />
          <button className="btn ghost icon" aria-pressed={focus}
            title={focus ? 'Bring the overlays back (\\)' : 'Hide every overlay (\\)'}
            onClick={() => controlsRef.current?.toggleFocus()}>
            <Icon name="expand" size={16} />
          </button>
          <ThemeToggle onChange={() => { stageRef.current?.repalette(); setThemeRev(r => r + 1); }} />
          <span className="vsep" />
          {/* Auto-plan needs an area to cover, so it only leads once the Site has a room. */}
          <button className={'btn' + ((store.groundViewDoc || store.doc).areas.some(a => a.kind === 'floor') ? ' primary' : '')} onClick={() => setDlg('plan')}>
            <Icon name="magic" size={16} />
            <span className="lbl-long">Auto-plan cameras</span><span className="lbl-short">Auto-plan</span>
          </button>
        </div>
      </header>

      {/* One toolbar row when there is room: the offline controls sit beside the main actions and wrap below on narrow screens. */}
      <div className="editor-toolbar">
      <div className="editor-subbar">
        <span className="editor-section-label"><Icon name="layers" size={16} />Site design</span>
        <div className="editor-actions">
          <button className="btn solid" onClick={() => save()} disabled={saving} title={autosave && !sampleMode ? 'Changes save automatically. Save now keeps this point as its own version in History.' : undefined}><Icon name="save" size={15} />{sampleMode ? 'Save copy' : 'Save'}</button>
          {!sampleMode && <NativeFieldTools store={store} save={save} disabled={saving || loading || !!pendingDraft || remoteChanged}
            onReviewWallHeight={openWallHeight}
            projectName={project?.name || 'Site'} revision={rev} accountID={user.id}
            onOpenPlanFinding={finding => {
              const account = currentOfflineAccountKey(), path = window.location.pathname, epoch = planNavigationEpoch.current;
              const assertCurrent = () => { if (epoch !== planNavigationEpoch.current || currentOfflineAccountKey() !== account || store.projectId !== id || window.location.pathname !== path) throw new Error('The Site or account changed. Open Plan checks again.'); };
              setPlanFindingRequest({ finding, projectID: id, account, path, assertCurrent });
            }}
            saveState={offlineRecord?.creation || offlineRecord?.pending?.length || offlineRecord?.blocked || offlineVersion !== undefined && offlineRecord?.generation !== offlineVersion
              ? 'saved-on-device' : Number.isInteger(rev) && rev > 0 ? 'saved-online' : 'unknown'} />}
          <button className="btn" onClick={openCatalogue}><Icon name="camera" size={15} />Camera library</button>
          <ToolbarPanel label="Site actions" className="site-actions-menu" open={siteActionsOpen} onOpenChange={setSiteActionsOpen}>
            <div className="site-action-list" onClick={e => { if (e.target.closest('button')) setSiteActionsOpen(false); }}>
              <button className="btn" onClick={openHistory}><Icon name="clock" />History</button>
              {!sampleMode && professional && <button className="btn" onClick={() => setSavedHistoryOpen(true)}><Icon name="clock" />Saved Undo</button>}
              <button className="btn" onClick={openShares}><Icon name="share" />Share</button>
              {!sampleMode && <button className="btn" onClick={() => setDlg('backup')}><Icon name="export" />Download Site backup</button>}
              <button className="btn" onClick={() => { if (!store.cov) store.evaluate(); setDlg('report'); }}><Icon name="report" />Report</button>
              <button className="btn" onClick={exportPng}><Icon name="export" />Export PNG</button>
              <button className="btn" onClick={() => fileRef.current?.click()}><Icon name="file-import" />Import PDF / image</button>
              <button className="btn" onClick={() => cadRef.current?.click()}><Icon name="import" />Import DXF</button>
              <button className="btn" onClick={exportDrawing}><Icon name="file-export" />Export floor DXF</button>
              {!sampleMode && <label className="check site-action-setting" onClick={e => e.stopPropagation()}><input type="checkbox" checked={autosave} onChange={e => { setAutosaveEnabled(e.target.checked); setAutosave(e.target.checked); setAutosaveFailures(0); }} /> Autosave on this device</label>}
              {/* The phone header has no room for these, so they live here on narrow screens. */}
              <div className="site-action-setting phone-only" onClick={e => e.stopPropagation()}><span>Colour theme</span><ThemeToggle onChange={() => { stageRef.current?.repalette(); setThemeRev(r => r + 1); }} /></div>
              <label className="site-action-setting tool-mode-setting" onClick={e => e.stopPropagation()}>Tools on this device<select aria-label="Tools on this device" value={professional ? 'professional' : 'simple'} onChange={e => setToolMode(e.target.value)}><option value="simple">Simple · planning essentials</option><option value="professional">Professional · survey checks and evidence</option></select></label>
            </div>
          </ToolbarPanel>
        </div>
        <span className="grow" />
        <span className="editor-site-status"><span />{isGroundLevel(store.doc) ? 'Site ground' : store.doc.floorName || 'Floor'} · {siteMode(store.groundViewDoc)}</span>
      </div>

      {!sampleMode && <OfflineSiteControls id={id} onRecord={receiveOfflineRecord} onPrepared={() => setLoadAttempt(n => n + 1)} dirty={store.dirty}
        onReload={() => setLoadAttempt(n => n + 1)} onRecover={doc => { historySession.current?.detach('This recovered backup uses current-session Undo. Earlier saved history remains available for review.'); store.setDoc(doc, { markClean: false, preserveLocks: true }); toast('Device backup opened for review. Save when you are ready to apply it.', 'ok'); }} />}
      </div>
      {!sampleMode && professional && (historyStatus.eligible || offlineVersion !== undefined && historyStatus.reason) && <div className="editor-subbar" aria-label="Saved Undo status">
        <span role="status">{historyStatus.eligible ? historyStatus.state === 'empty' ? 'Save also keeps Undo on this device.' : store.dirty ? 'New Undo steps are kept when you Save.' : 'Saved Undo is available on this device.' : historyStatus.reason}</span>
        <button className="btn" onClick={() => setSavedHistoryOpen(true)}>Review saved Undo</button>
      </div>}
      {!sampleMode && historySaveIssue && <div className="editor-subbar" role="alert" aria-label="Undo storage issue" style={{ flexWrap: 'wrap', height: 'auto' }}>
        <span>{historySaveIssue.message} {historySaveIssue.drawing ? 'The proposed drawing was not saved. Close its review before continuing.' : 'The plan has not been saved.'} You can retry Save, or save only the current plan. Undo stays available until this editor closes; earlier saved history and drawings remain for review.</span>
        <button className="btn" disabled={saving || !!pendingDraft} onClick={() => save('', { planOnly: true })}>Save plan only</button>
      </div>}
      {store.editError&&<div className="toast bad" style={{position:'fixed',top:78,left:'50%',transform:'translateX(-50%)',zIndex:90,width:'max-content',maxWidth:'calc(100vw - 32px)'}} role="alert"><span>{store.editError}</span><button className="btn" onClick={()=>{store.editError='';store.emit();}}>Dismiss</button></div>}
      {remoteChanged && <div className="editor-subbar" role="status">
        <span>This Site has a newer saved version. {store.dirty ? 'Your edits are kept here; save a separate copy before opening it.' : 'Open it to see the latest changes.'}</span>
        {store.dirty && <button className="btn" disabled={saving} onClick={async () => {
          if (saveInFlight.current) return;
          saveInFlight.current = true; setSaving(true);
          try {
            const before = JSON.stringify(store.toDocument());
            await api.projects.create((project.name + ' — recovered edits').slice(0,120), JSON.parse(before));
            toast('Your edits were saved as a separate Site.', 'ok');
            if (JSON.stringify(store.toDocument()) === before) { store.markSaved(); clearDraft(user.id, id); }
          } catch (error) { toast(error.message, 'bad'); }
          finally { saveInFlight.current = false; setSaving(false); }
        }}>Save my edits as a copy</button>}
        <button className="btn" disabled={store.dirty || saving} onClick={() => setLoadAttempt(n => n + 1)}>Open latest version</button>
      </div>}
      {sample && <div className="editor-context">
      <ToolbarPanel label="Sample guide" className="sample-guide-menu" open={sampleGuideOpen} onOpenChange={setSampleGuideOpen}>
        <p><strong>{sample.name}</strong></p><p>{sample.description}</p><ol>{sample.guidance.map(g => <li key={g}>{g}</li>)}</ol><p className="hint">{sample.assumptions}</p><p className="hint">{sampleDraft ? 'Save to keep your changes.' : 'Explore freely. Editing creates your own copy; the original stays protected.'}</p>
      </ToolbarPanel>
      </div>}
      <Modal open={blocker.state === 'blocked'} onClose={() => { if (!saving) blocker.reset?.(); }} width={480}>
        <div className="dhead"><h2>Save changes before leaving?</h2></div>
        <div className="dbody"><p>Your plan has unsaved edits.</p>{saveError && <p className="formerror" role="alert">{saveError}</p>}</div>
        <div className="dfoot"><button className="btn" disabled={saving} onClick={() => blocker.reset()}>Stay</button><button className="btn danger" disabled={saving} onClick={() => { clearDraft(user.id, sampleMode ? 'sample:' + id : id); store.markSaved(); blocker.proceed(); }}>Discard changes</button><button className="btn primary" disabled={saving} onClick={async () => { if (await save()) blocker.proceed(); }}>{saving ? 'Saving…' : 'Save and leave'}</button></div>
      </Modal>
      <Modal open={!!pendingDraft} onClose={() => {}} width={520}>
        <div className="dhead"><h2>Recover unsaved edits?</h2></div>
        <div className="dbody"><p>This browser kept a draft from {pendingDraft && new Date(pendingDraft.savedAt).toLocaleString()}.</p>{pendingDraft && pendingDraft.baseRev !== rev && <p>The server has a different revision. Recovering opens your draft for review; it does not overwrite the saved project.</p>}</div>
        <div className="dfoot"><button className="btn" onClick={() => { clearDraft(user.id, sampleMode ? 'sample:' + id : id); setPendingDraft(null); }}>Discard draft</button><button className="btn primary" onClick={() => { try { historySession.current?.detach('This recovered draft uses current-session Undo. Earlier saved history remains available for review.'); store.setDoc(pendingDraft.doc, { markClean: false, preserveLocks: true }); setRev(pendingDraft.baseRev ?? 0); loadBackground(store.doc); setPendingDraft(null); } catch(error) { toast(error.message,'bad'); } }}>Recover draft</button></div>
      </Modal>
      <Workspace
        store={store}
        navigationDisabled={saving || loading || !!pendingDraft || remoteChanged}
        planFindingRequest={planFindingRequest}
        stageRef={stageRef}
        controlsRef={controlsRef}
        onDeliverAction={action => action === 'save' ? save() : action === 'share' ? openShares() : setDlg(action)}
        onFocusChange={setFocus}
        onDimensionChange={setDimension}
        themeRev={themeRev}
        onCalibrate={onCalibrate}
        onUploadBg={() => fileRef.current?.click()}
        onEditModel={mid => sampleMode ? materializeSample({ modelName: store.model(mid)?.name }) : setModelDlg({ id: mid })}
        onSyncModels={onSyncModels}
        onBrowseModels={openCatalogue}
        onReviewWallHeight={sampleMode ? undefined : openWallHeight}
        onReviewFloorElevation={sampleMode ? undefined : openFloorElevation}
      />

      {wallHeightRequest && <WallHeightReview key={`${wallHeightRequest.wallID}:${wallHeightRequest.floorID}`} store={store} save={save}
        disabled={saving || loading || !!pendingDraft || remoteChanged} initialWallID={wallHeightRequest.wallID}
        returnFocusRef={wallHeightRequest.returnFocusRef} onClose={() => setWallHeightRequest(null)} />}

      {floorElevationRequest && <FloorElevationReview key={floorElevationRequest.floorID} store={store} save={save}
        disabled={saving || loading || !!pendingDraft || remoteChanged} initialFloorID={floorElevationRequest.floorID}
        returnFocusRef={floorElevationRequest.returnFocusRef} onClose={() => setFloorElevationRequest(null)} />}

      <input ref={fileRef} type="file" accept="image/*,.pdf,application/pdf" hidden onChange={onPickImage} />

      <input ref={cadRef} type="file" accept=".dxf" hidden onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f && f.size <= 8 * 1024 * 1024) setCadFile(f); else if (f) toast('Choose a DXF under 8 MB.', 'bad'); }} />
      <PdfImport file={pdfFile} onClose={() => setPdfFile(null)} onImport={(file, source) => placeBackground(file, source, drawingPick.current)} />
      {drawingReview && <ReferenceDrawingReview key={drawingReview.target.sequence} prepared={drawingReview.prepared} target={drawingReview.target} store={store} save={save} commitReferenceDrawing={commitReferenceDrawing} disabled={saving} isCurrent={() => drawingTargetCurrent(drawingReview.target)} onClose={() => setDrawingReview(null)} />}
      <DxfImport file={cadFile} store={store} onClose={() => setCadFile(null)} onDone={() => setTimeout(() => stageRef.current?.fit(), 30)} />
      <PlacedModelUpdates open={dlg === 'modelUpdates'} onClose={() => setDlg(null)} store={store} />
      <CatalogueDialog open={dlg === 'catalogue'} initialMode={libraryReturn ? 'add' : 'browse'} returnLabel={libraryReturn ? 'Return to planner' : undefined} onClose={() => { setDlg(libraryReturn); setLibraryReturn(null); }} store={store} />
      <PlanDialog open={dlg === 'plan' || dlg === 'quotes'} initialSection={dlg === 'quotes' ? 'quotes' : 'settings'} onClose={() => setDlg(null)} store={store} onFix={tool => controlsRef.current?.chooseTool(tool)}
        onDone={r => { const bom = billOfMaterials(store.doc); toast(`${r.cams.length} cameras placed · ${r.metPct.toFixed(1)}% of floor meets its grade · ${totalLabel(bom.total, bom.unknown, store.doc.opt.currency)}.${r.entries?.length ? ` Entries: ${r.entries.filter(e => e.complete).length}/${r.entries.length} directions identified.` : ''}${r.targetReached ? '' : ' Review unmet floor, zone, entry or overlap goals in Analysis.'}${r.constraintNotes?.length ? ' ' + r.constraintNotes.join(' ') : ''}`, r.targetReached ? 'ok' : ''); }} />
      <ModelDialog open={!!modelDlg} onClose={() => setModelDlg(null)} store={store}
        modelId={modelDlg?.id} canEditShared={user?.isAdmin} onSave={onSaveModel} onDelete={onDeleteModel} />
      <ReportDialog open={dlg === 'report'} onClose={() => setDlg(null)} store={store}
        projectName={project?.name || 'Site'} revision={rev} />
      <CalibrateDialog open={dlg === 'calib'} onClose={() => setDlg(null)}
        pending={pendingCal} onApply={applyCalibration} doc={store.doc} />
      {!sampleMode && <SavedEditHistory open={savedHistoryOpen} onClose={() => setSavedHistoryOpen(false)} siteID={id} editorOpen />}
      <HistoryDialog open={dlg === 'history'} onClose={() => setDlg(null)}
        versions={versions} current={rev} onRestore={restore} busy={saving} />
      {!sampleMode && <SiteBackup open={dlg === 'backup'} onClose={() => setDlg(null)} id={id} dirty={store.dirty} onSave={() => save('Site saved before backup')} />}
      <ShareDialog open={dlg === 'share'} onClose={() => setDlg(null)} revision={rev} dirty={store.dirty}
        shares={shares} onSave={save} onCreate={createShare} onRevoke={revokeShare} />
    </div>
  );
}
